import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Menu, Volume2, VolumeX, Settings, LogOut, Users, Move, RotateCcw } from 'lucide-react';
import { IGRA_ROOM_ASSETS } from '../components/belot/igraRoomAssetUrls';
import { useAudio } from './AudioProvider';
import { cn } from './utils/cn';

const FISKI_IMG = `${process.env.PUBLIC_URL || ''}/fiski.png`;
const BUCKS_IMG = `${process.env.PUBLIC_URL || ''}/bucks.png`;

function formatCompactBalance(value) {
  return Number(value).toLocaleString('ru-RU');
}

/** Примерная высота до первого измерения (мобильный ряд балансов + шапка). */
const FLOATING_HEADER_FALLBACK_PX = 104;

export function GameIndustrialHeader({
  tableName,
  blindsLabel,
  playersCount,
  maxPlayers,
  balance,
  bucksBalance,
  onLeave,
  /** Локальная сека: переключить режим перетаскивания мест по столу. */
  onSeatArrangeToggle,
  seatArrangeMode = false,
  /** Сбросить сохранённые смещения мест (показывается в режиме расстановки). */
  onSeatLayoutReset,
  /** Дополнительные контролы справа (например, пресеты рассадки). */
  extraControls = null,
  /**
   * Плавающая шапка как `LobbyBottomNav`: `fixed top-0` + размытие; под контентом — спейсер по фактической высоте.
   */
  floating = false,
  /** Комната «игра»: фон `header-game-png`, назад 60×60 вместо меню/выхода. */
  chromeVariant = 'default',
}) {
  const isIgraChrome = chromeVariant === 'igra';
  const { isMuted, toggleMute } = useAudio();
  const measureRef = useRef(null);
  const [floatingBarPx, setFloatingBarPx] = useState(null);
  const [animatedBalance, setAnimatedBalance] = useState(
    Number.isFinite(Number(balance)) ? Math.max(0, Math.floor(Number(balance))) : null,
  );
  const [animatedBucksBalance, setAnimatedBucksBalance] = useState(
    Number.isFinite(Number(bucksBalance)) ? Math.max(0, Math.floor(Number(bucksBalance))) : null,
  );
  const balanceAnimRef = useRef(/** @type {number|null} */ (null));
  const bucksAnimRef = useRef(/** @type {number|null} */ (null));
  const balanceValueRef = useRef(Number.isFinite(Number(balance)) ? Math.max(0, Math.floor(Number(balance))) : 0);
  const bucksValueRef = useRef(
    Number.isFinite(Number(bucksBalance)) ? Math.max(0, Math.floor(Number(bucksBalance))) : 0,
  );

  useEffect(() => {
    return () => {
      if (balanceAnimRef.current != null) cancelAnimationFrame(balanceAnimRef.current);
      if (bucksAnimRef.current != null) cancelAnimationFrame(bucksAnimRef.current);
    };
  }, []);

  useEffect(() => {
    if (!Number.isFinite(Number(balance))) {
      balanceValueRef.current = 0;
      setAnimatedBalance(null);
      return;
    }
    const target = Math.max(0, Math.floor(Number(balance)));
    if (balanceAnimRef.current != null) {
      cancelAnimationFrame(balanceAnimRef.current);
      balanceAnimRef.current = null;
    }
    let startTs = /** @type {number|null} */ (null);
    const from = balanceValueRef.current;
    const delta = target - from;
    if (!delta) {
      setAnimatedBalance(target);
      return;
    }
    const duration = Math.min(1400, Math.max(380, Math.abs(delta) * 0.22));
    const step = (ts) => {
      if (startTs == null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + delta * eased);
      balanceValueRef.current = next;
      setAnimatedBalance(next);
      if (t < 1) {
        balanceAnimRef.current = requestAnimationFrame(step);
      } else {
        balanceValueRef.current = target;
        balanceAnimRef.current = null;
      }
    };
    balanceAnimRef.current = requestAnimationFrame(step);
    return () => {
      if (balanceAnimRef.current != null) {
        cancelAnimationFrame(balanceAnimRef.current);
        balanceAnimRef.current = null;
      }
    };
  }, [balance]);

  useEffect(() => {
    if (!Number.isFinite(Number(bucksBalance))) {
      bucksValueRef.current = 0;
      setAnimatedBucksBalance(null);
      return;
    }
    const target = Math.max(0, Math.floor(Number(bucksBalance)));
    if (bucksAnimRef.current != null) {
      cancelAnimationFrame(bucksAnimRef.current);
      bucksAnimRef.current = null;
    }
    let startTs = /** @type {number|null} */ (null);
    const from = bucksValueRef.current;
    const delta = target - from;
    if (!delta) {
      setAnimatedBucksBalance(target);
      return;
    }
    const duration = Math.min(1300, Math.max(320, Math.abs(delta) * 0.25));
    const step = (ts) => {
      if (startTs == null) startTs = ts;
      const t = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + delta * eased);
      bucksValueRef.current = next;
      setAnimatedBucksBalance(next);
      if (t < 1) {
        bucksAnimRef.current = requestAnimationFrame(step);
      } else {
        bucksValueRef.current = target;
        bucksAnimRef.current = null;
      }
    };
    bucksAnimRef.current = requestAnimationFrame(step);
    return () => {
      if (bucksAnimRef.current != null) {
        cancelAnimationFrame(bucksAnimRef.current);
        bucksAnimRef.current = null;
      }
    };
  }, [bucksBalance]);

  const hasBalances =
    Number.isFinite(Number(animatedBucksBalance)) || Number.isFinite(Number(animatedBalance));
  const playersPill = (
    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-slate-200">
      <Users className="h-3 w-3 text-slate-300" />
      <span className="font-semibold tabular-nums">
        {playersCount}/{maxPlayers}
      </span>
    </span>
  );
  const balancesCompact = hasBalances ? (
    <span className="inline-flex items-center gap-1">
      {Number.isFinite(Number(animatedBucksBalance)) && (
        <span className="flex max-w-[6.9rem] items-center rounded-full border border-emerald-500/30 bg-gradient-to-r from-[#1a2f1a] to-[#0f1f0f] px-1 py-0.5">
          <span className="flex h-4 w-4 items-center justify-center">
            <img src={BUCKS_IMG} alt="" className="h-3 w-3 object-contain" draggable={false} />
          </span>
          <span className="truncate px-1 text-[9px] font-bold tabular-nums text-white">
            {formatCompactBalance(animatedBucksBalance)}
          </span>
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-[9px] font-black leading-none text-white">
            +
          </span>
        </span>
      )}
      {Number.isFinite(Number(animatedBalance)) && (
        <span className="flex max-w-[7.6rem] items-center rounded-full border border-amber-500/30 bg-gradient-to-r from-[#2f2a1a] to-[#1f1a0f] px-1 py-0.5">
          <span className="flex h-4 w-4 items-center justify-center">
            <img src={FISKI_IMG} alt="" className="h-3 w-3 object-contain" draggable={false} />
          </span>
          <span className="truncate px-1 text-[9px] font-bold tabular-nums text-white">
            {formatCompactBalance(animatedBalance)}
          </span>
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-[9px] font-black leading-none text-white">
            +
          </span>
        </span>
      )}
    </span>
  ) : null;
  useLayoutEffect(() => {
    if (!floating) {
      setFloatingBarPx(null);
      return undefined;
    }
    const el = measureRef.current;
    if (!el) return undefined;
    const apply = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      setFloatingBarPx((prev) => (prev === next ? prev : next));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => apply());
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    floating,
    balance,
    bucksBalance,
    tableName,
    blindsLabel,
    playersCount,
    maxPlayers,
    seatArrangeMode,
    animatedBalance,
    animatedBucksBalance,
    chromeVariant,
  ]);

  const balancesWide = hasBalances ? (
    <span className="inline-flex items-center gap-1.5">
      {Number.isFinite(Number(animatedBucksBalance)) && (
        <span className="flex max-w-[8.4rem] items-center rounded-full border border-emerald-500/30 bg-gradient-to-r from-[#1a2f1a] to-[#0f1f0f] px-1 py-0.5">
          <span className="flex h-5 w-5 items-center justify-center">
            <img src={BUCKS_IMG} alt="" className="h-3.5 w-3.5 object-contain" draggable={false} />
          </span>
          <span className="truncate px-1 text-[10px] font-bold tabular-nums text-white">
            {formatCompactBalance(animatedBucksBalance)}
          </span>
          <span className="flex h-4.5 w-4.5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-[10px] font-black leading-none text-white">
            +
          </span>
        </span>
      )}
      {Number.isFinite(Number(animatedBalance)) && (
        <span className="flex max-w-[9.2rem] items-center rounded-full border border-amber-500/30 bg-gradient-to-r from-[#2f2a1a] to-[#1f1a0f] px-1 py-0.5">
          <span className="flex h-5 w-5 items-center justify-center">
            <img src={FISKI_IMG} alt="" className="h-3.5 w-3.5 object-contain" draggable={false} />
          </span>
          <span className="truncate px-1 text-[10px] font-bold tabular-nums text-white">
            {formatCompactBalance(animatedBalance)}
          </span>
          <span className="flex h-4.5 w-4.5 items-center justify-center rounded-full bg-gradient-to-b from-green-400 to-green-600 text-[10px] font-black leading-none text-white">
            +
          </span>
        </span>
      )}
    </span>
  ) : null;

  /** Непрозрачный слой + тот же фон, что у комнаты «игра», иначе сквозь шапку виден bg-background темы. */
  const igraShellSurfaceStyle = isIgraChrome
    ? {
        backgroundColor: '#151311',
        backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.bg)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  const shellClass = cn(
    isIgraChrome
      ? cn(
          'relative border-0 px-2 shadow-none backdrop-blur-none sm:px-3',
          floating
            ? 'fixed left-0 right-0 top-0 z-[32] pb-2 pt-[max(6px,env(safe-area-inset-top,0px))]'
            : 'z-20 py-2 sm:py-2.5',
        )
      : cn(
          'border-b border-white/10 bg-gradient-to-b from-[#101a29]/95 to-[#0b1320]/90 px-3 shadow-[0_10px_32px_rgba(0,0,0,0.35)] backdrop-blur-md sm:px-4',
          floating
            ? 'fixed left-0 right-0 top-0 z-[32] border-white/[0.06] pb-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))] sm:pb-2.5 sm:pt-[max(0.625rem,env(safe-area-inset-top,0px))]'
            : 'relative z-20 py-2 sm:py-2.5',
        ),
  );

  const igraHeaderBg = (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[min(97px,26vw)] bg-no-repeat [filter:drop-shadow(0_4px_4px_#000000)] sm:h-[min(97px,22vw)]"
      aria-hidden
      style={{
        backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.header)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
      }}
    />
  );

  const controlsCluster = (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-foreground transition hover:border-cyan-300/35 hover:bg-cyan-400/10"
        aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
        onClick={toggleMute}
      >
        {isMuted ? <VolumeX className="h-4 w-4 text-slate-400" /> : <Volume2 className="h-4 w-4 text-slate-100" />}
      </button>
      {typeof onSeatArrangeToggle === 'function' ? (
        <button
          type="button"
          onClick={onSeatArrangeToggle}
          aria-pressed={Boolean(seatArrangeMode)}
          title={
            seatArrangeMode
              ? 'Закончить расстановку мест (сохранено в браузере для этого числа игроков)'
              : 'Расставить стол: перетащите блоки мест; только на вашем экране'
          }
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border text-foreground transition',
            seatArrangeMode
              ? 'border-amber-400/55 bg-amber-500/15 text-amber-100 shadow-[0_0_14px_rgba(251,191,36,0.2)] hover:border-amber-300/70 hover:bg-amber-500/22'
              : 'border-white/10 bg-white/[0.03] hover:border-cyan-300/35 hover:bg-cyan-400/10'
          )}
        >
          <Move className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      {seatArrangeMode && typeof onSeatLayoutReset === 'function' ? (
        <button
          type="button"
          onClick={onSeatLayoutReset}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-100 transition hover:border-cyan-300/35 hover:bg-cyan-400/10"
          aria-label="Сбросить позиции мест"
          title="Сбросить сохранённые позиции для этого числа игроков"
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      {extraControls}
      <button
        type="button"
        className="hidden h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-100 transition hover:border-cyan-300/35 hover:bg-cyan-400/10 sm:flex"
        aria-label="Настройки"
      >
        <Settings className="h-4 w-4" />
      </button>
      {!isIgraChrome ? (
        <button
          type="button"
          onClick={onLeave}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-orange-300/25 bg-orange-400/5 text-orange-300 transition hover:border-orange-300/45 hover:bg-orange-400/12"
          aria-label="Покинуть стол"
        >
          <LogOut className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );

  const titleBlock = (
    <div className="min-w-0 flex-1 px-1 sm:px-1.5">
      <h1 className="truncate text-[12px] font-black tracking-[0.02em] text-slate-100 sm:max-w-[260px] sm:text-sm">
        {tableName}
      </h1>
      <div className="mt-1 hidden max-w-full items-center justify-center gap-1.5 text-[10px] sm:flex">
        {playersPill}
        {balancesWide}
      </div>
    </div>
  );

  const headerInner = isIgraChrome ? (
    <>
      <div className="relative overflow-visible">
        {igraHeaderBg}
        <div className="relative z-[1] flex items-center justify-between gap-1 sm:gap-2">
          <button
            type="button"
            className="h-[60px] w-[60px] shrink-0 cursor-pointer border-0 bg-transparent bg-contain bg-center bg-no-repeat p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-cyan-400/85"
            aria-label="В лобби"
            onClick={onLeave}
            style={{ backgroundImage: `url(${JSON.stringify(IGRA_ROOM_ASSETS.back)})` }}
          />
          {titleBlock}
          {controlsCluster}
        </div>
      </div>
      <div className="relative z-[1] mt-1.5 flex items-center justify-between gap-2 text-[10px] sm:hidden">
        {playersPill}
        {balancesCompact}
      </div>
    </>
  ) : (
    <>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-300/25 to-transparent" />
      <div className="flex items-center justify-between gap-1.5 sm:gap-2">
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-foreground transition hover:border-cyan-300/40 hover:bg-cyan-400/10"
          aria-label="Меню"
          title="Меню (скоро)"
          onClick={() => {}}
        >
          <Menu className="h-4.5 w-4.5" />
        </button>
        {titleBlock}
        {controlsCluster}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] sm:hidden">
        {playersPill}
        {balancesCompact}
      </div>
    </>
  );

  if (floating) {
    return (
      <>
        <header ref={measureRef} className={shellClass} style={igraShellSurfaceStyle}>
          {headerInner}
        </header>
        <div
          className="shrink-0"
          style={{
            height: floatingBarPx ?? FLOATING_HEADER_FALLBACK_PX,
            ...(isIgraChrome ? igraShellSurfaceStyle : {}),
          }}
          aria-hidden="true"
        />
      </>
    );
  }

  return (
    <header className={shellClass} style={igraShellSurfaceStyle}>
      {headerInner}
    </header>
  );
}
